import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  DataTable,
  Spinner,
  Banner,
} from "@shopify/polaris";
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch";
import { formatCurrency } from "../utils/cost-calculations";

interface DashboardData {
  openPOs: number;
  draftPOs: number;
  submittedPOs: number;
  pendingReceives: number;
  fullyReceivedThisMonth: number;
  totalVendors: number;
  recentPOs: Array<{
    id: string;
    poNumber: string;
    vendorName: string;
    status: string;
    total: number;
    createdAt: string;
  }>;
}

const STATUS_TONES: Record<string, "info" | "attention" | "success" | "critical" | undefined> = {
  draft: "info",
  submitted: "attention",
  partially_received: "attention",
  fully_received: "success",
  cancelled: "critical",
  closed: undefined,
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  submitted: "Submitted",
  partially_received: "Partial",
  fully_received: "Received",
  cancelled: "Cancelled",
  closed: "Closed",
};

export default function Dashboard() {
  const authFetch = useAuthenticatedFetch();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch("/api/purchase-orders/dashboard");
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setData(json.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    load();
  }, []);

  if (loading || !data) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const recentRows = data.recentPOs.map((po) => [
    po.poNumber,
    po.vendorName,
    new Date(po.createdAt).toLocaleDateString(),
    STATUS_LABELS[po.status] || po.status,
    formatCurrency(po.total),
  ]);

  return (
    <Page
      title="Dashboard"
      subtitle="PetMax Purchase Orders Overview"
      secondaryActions={[
        { content: "All POs", url: "/purchase-orders" },
        { content: "Vendors", url: "/" },
        { content: "Settings", url: "/settings" },
      ]}
    >
      {error && (
        <Layout.Section>
          <Banner tone="critical">
            <p>{error}</p>
          </Banner>
        </Layout.Section>
      )}

      <Layout>
        {/* Summary Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap>
            <Card>
              <BlockStack gap="200" inlineAlign="center">
                <Text as="span" tone="subdued">Open POs</Text>
                <Text variant="headingXl" as="p">{String(data.openPOs)}</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200" inlineAlign="center">
                <Text as="span" tone="subdued">Drafts</Text>
                <Text variant="headingXl" as="p">{String(data.draftPOs)}</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200" inlineAlign="center">
                <Text as="span" tone="subdued">Awaiting Delivery</Text>
                <Text variant="headingXl" as="p">
                  {String(data.pendingReceives)}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200" inlineAlign="center">
                <Text as="span" tone="subdued">Received This Month</Text>
                <Text variant="headingXl" as="p">
                  {String(data.fullyReceivedThisMonth)}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200" inlineAlign="center">
                <Text as="span" tone="subdued">Active Vendors</Text>
                <Text variant="headingXl" as="p">
                  {String(data.totalVendors)}
                </Text>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Recent POs */}
        <Layout.Section>
          <Card padding="0">
            <div style={{ padding: "16px 16px 0" }}>
              <Text variant="headingMd" as="h2">
                Recent Purchase Orders
              </Text>
            </div>
            {recentRows.length === 0 ? (
              <div style={{ padding: "16px" }}>
                <Text as="p" tone="subdued">
                  No purchase orders yet.
                </Text>
              </div>
            ) : (
              <DataTable
                columnContentTypes={["text", "text", "text", "text", "numeric"]}
                headings={["PO #", "Vendor", "Date", "Status", "Total"]}
                rows={recentRows}
                hoverable
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
